import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Menu, X, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
const topics = [{
  id: 'ducted',
  title: 'What is a ducted system?'
}, {
  id: 'cassette-vs-wall',
  title: 'Cassette vs wall mounted'
}, {
  id: 'office',
  title: 'Best systems for offices'
}, {
  id: 'retail',
  title: 'Best systems for retail'
}];
export function SystemGuides() {
  const [activeId, setActiveId] = useState<string>('');
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    // Handle initial hash routing
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => scrollTo(id), 100);
    }
  }, [location.hash]);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    topics.forEach(({
      id
    }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileTocOpen(false);
  };
  return <div className="min-h-screen bg-white font-sans text-primary-dark" data-id="element-2032">
      <section className="bg-[#E8F0F8] pt-32 pb-20" data-id="element-2033">
        <div className="container mx-auto px-6" data-id="element-2034">
          <div className="max-w-4xl" data-id="element-2035">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-dark/60 mb-6" data-id="element-2036">
              <Link to="/knowledge-hub" className="hover:text-accent transition-colors" data-id="element-2037">
                Resources
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-2038" />
              <span className="text-primary-dark" data-id="element-2039">System Guides</span>
            </div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-2040">
              Commercial AC system guides
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-2041">
              Learn about the different types of commercial air conditioning
              systems, compare options, and discover which setup is best suited
              for your specific building and sector.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16" data-id="element-2042">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-2043">
          <div className="lg:hidden" data-id="element-2044">
            <button onClick={() => setIsMobileTocOpen(!isMobileTocOpen)} className="flex items-center gap-2 text-primary-dark font-medium w-full justify-between bg-[#E8F0F8] p-4 rounded-xl" data-id="element-2045">
              <span data-id="element-2046">Table of contents</span>
              {isMobileTocOpen ? <X className="w-5 h-5" data-id="element-2047" /> : <Menu className="w-5 h-5" data-id="element-2048" />}
            </button>
            {isMobileTocOpen && <div className="mt-2 bg-white border border-primary-dark/10 rounded-xl p-4 shadow-lg" data-id="element-2049">
                <nav className="flex flex-col gap-3" data-id="element-2050">
                  {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/70 hover:text-primary-dark'}`} data-id="element-2051">
                      {topic.title}
                    </button>)}
                </nav>
              </div>}
          </div>

          <aside className="hidden lg:block w-64 shrink-0" data-id="element-2052">
            <div className="sticky top-32" data-id="element-2053">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary-dark/50 mb-6" data-id="element-2054">
                Contents
              </h3>
              <nav className="flex flex-col gap-4 border-l-2 border-primary-dark/10 pl-4" data-id="element-2055">
                {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors relative ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/60 hover:text-primary-dark'}`} data-id="element-2056">
                    {activeId === topic.id && <motion.div layoutId="activeToc" className="absolute -left-[18px] top-1/2 -translate-y-1/2 w-0.5 h-full bg-accent" data-id="element-2057" />}
                    {topic.title}
                  </button>)}
              </nav>
            </div>
          </aside>

          <main className="flex-1 max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:text-primary-dark prose-p:text-primary-dark/80 prose-a:text-accent prose-li:text-primary-dark/80" data-id="element-2058">
            {/* Topic 1 */}
            <section id="ducted" className="mb-20 scroll-mt-32" data-id="element-2059">
              <h2 className="text-3xl font-serif mb-6" data-id="element-2060">
                What is a ducted air conditioning system?
              </h2>
              <p data-id="element-2061">
                A ducted air conditioning system is the ultimate solution for
                discreet, whole-building climate control. Unlike wall-mounted or
                cassette units where the machinery is visible in the room, a
                ducted system hides the main fan coil unit within a suspended
                ceiling or roof void.
              </p>
              <p data-id="element-2062">
                Conditioned air is then distributed through a network of
                concealed insulated ducts and delivered into the room via subtle
                grilles or diffusers. These grilles can be designed to match the
                interior aesthetics, making the system virtually invisible.
              </p>
              <p data-id="element-2063">
                Because the mechanical components are hidden away from the
                occupied space, ducted systems are exceptionally quiet. They are
                the preferred choice for high-end offices, luxury retail spaces,
                and premium hospitality venues where aesthetics and acoustics
                are paramount.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-2064">
                <h4 className="text-xl font-serif mb-4" data-id="element-2065">
                  Considering a ducted system?
                </h4>
                <p className="mb-6 text-base" data-id="element-2066">
                  Learn more about the benefits and technical requirements.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-2067">
                  <Link to="/services/ducted" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-2068">
                    See the related system <ArrowRight className="w-4 h-4" data-id="element-2069" />
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 bg-white hover:bg-white/80 text-primary-dark px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-2070">
                    Talk to us about the right setup
                  </Link>
                </div>
              </div>
            </section>

            {/* Topic 2 */}
            <section id="cassette-vs-wall" className="mb-20 scroll-mt-32" data-id="element-2071">
              <h2 className="text-3xl font-serif mb-6" data-id="element-2072">
                Cassette vs wall mounted air conditioning
              </h2>
              <p data-id="element-2073">
                When choosing between cassette and wall-mounted air conditioning
                for a commercial space, the decision usually comes down to room
                size, ceiling type, and budget.
              </p>
              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-2074">
                Cassette Systems
              </h3>
              <p data-id="element-2075">
                Cassette units are mounted into a suspended ceiling, with only a
                flush fascia panel visible. They distribute air in four
                directions (360 degrees in modern units), providing very even
                temperature control across large, open-plan areas. They are
                ideal for open offices and retail stores but require a ceiling
                void to house the unit.
              </p>
              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-2076">
                Wall Mounted Systems
              </h3>
              <p data-id="element-2077">
                Wall-mounted units are affixed high up on a wall. They blow air
                in a single direction (outwards and downwards). They are highly
                cost-effective, quick to install, and perfect for individual
                rooms, server rooms, or spaces without a suspended ceiling.
                However, in very large open spaces, a single wall unit may
                struggle to distribute air evenly to the far corners.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-2078">
                <h4 className="text-xl font-serif mb-4" data-id="element-2079">
                  Compare your options
                </h4>
                <p className="mb-6 text-base" data-id="element-2080">
                  Explore our detailed system pages to find the perfect fit.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-2081">
                  <Link to="/services/cassette" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-2082">
                    See cassette systems <ArrowRight className="w-4 h-4" data-id="element-2083" />
                  </Link>
                  <Link to="/services/wall-mounted" className="inline-flex items-center gap-2 bg-white hover:bg-white/80 text-primary-dark px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-2084">
                    See wall mounted systems <ArrowRight className="w-4 h-4" data-id="element-2085" />
                  </Link>
                </div>
              </div>
            </section>

            {/* Topic 3 */}
            <section id="office" className="mb-20 scroll-mt-32" data-id="element-2086">
              <h2 className="text-3xl font-serif mb-6" data-id="element-2087">
                Which commercial air conditioning system is best for an office?
              </h2>
              <p data-id="element-2088">
                The "best" system for an office depends entirely on the office
                layout. Modern offices typically employ a mix of systems to
                achieve optimal comfort and efficiency.
              </p>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-2089">
                <li data-id="element-2090">
                  <strong data-id="element-2091">Open-plan areas:</strong> Cassette systems or ducted
                  systems are best here. They provide even, draft-free air
                  distribution across large spaces where multiple people are
                  working.
                </li>
                <li data-id="element-2092">
                  <strong data-id="element-2093">Meeting rooms:</strong> Wall-mounted units or small
                  ducted units work well, allowing occupants to quickly adjust
                  the temperature for a specific meeting without affecting the
                  rest of the floor.
                </li>
                <li data-id="element-2094">
                  <strong data-id="element-2095">Server rooms:</strong> Dedicated wall-mounted units
                  are the standard, providing reliable, 24/7 cooling independent
                  of the main office system.
                </li>
                <li data-id="element-2096">
                  <strong data-id="element-2097">Premium boardrooms:</strong> Ducted systems are
                  preferred for their near-silent operation and invisible
                  aesthetics.
                </li>
              </ul>
              <p data-id="element-2098">
                For larger office buildings, these individual indoor units are
                usually connected to a central VRV or VRF outdoor system, which
                allows for simultaneous heating and cooling in different parts
                of the building while maximizing energy efficiency.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-2099">
                <h4 className="text-xl font-serif mb-4" data-id="element-2100">
                  Need an office HVAC design?
                </h4>
                <p className="mb-6 text-base" data-id="element-2101">
                  Our engineers design bespoke systems for optimal workplace
                  comfort.
                </p>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-2102">
                  Talk to us about the right setup{' '}
                  <ArrowRight className="w-4 h-4" data-id="element-2103" />
                </Link>
              </div>
            </section>

            {/* Topic 4 */}
            <section id="retail" className="mb-20 scroll-mt-32" data-id="element-2104">
              <h2 className="text-3xl font-serif mb-6" data-id="element-2105">
                Which air conditioning system is best for retail?
              </h2>
              <p data-id="element-2106">
                Retail environments present unique HVAC challenges: high
                footfall, constantly opening doors, dense lighting heat, and the
                need to keep customers comfortable enough to linger.
              </p>
              <p data-id="element-2107">
                <strong data-id="element-2108">Cassette systems</strong> are the most popular choice
                for standard retail spaces. They fit neatly into standard
                suspended ceilings, distribute air evenly across the shop floor,
                and don't take up valuable wall or floor space that could be
                used for merchandising.
              </p>
              <p data-id="element-2109">
                <strong data-id="element-2110">Ducted systems</strong> are heavily favoured by high-end
                boutiques, luxury brands, and modern industrial-chic spaces. The
                ductwork can either be completely hidden above a ceiling for a
                pristine look, or left exposed (spiral ducting) to contribute to
                an urban aesthetic.
              </p>
              <p data-id="element-2111">
                Retail spaces also frequently require{' '}
                <strong data-id="element-2112">over-door heaters or air curtains</strong> to create a
                thermal barrier at the entrance, preventing conditioned air from
                escaping when doors are open.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-2113">
                <h4 className="text-xl font-serif mb-4" data-id="element-2114">
                  Upgrading your retail space?
                </h4>
                <p className="mb-6 text-base" data-id="element-2115">
                  Ensure your climate control enhances the customer experience.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-2116">
                  <Link to="/services/cassette" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-2117">
                    See cassette systems <ArrowRight className="w-4 h-4" data-id="element-2118" />
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 bg-white hover:bg-white/80 text-primary-dark px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-2119">
                    Talk to us about the right setup
                  </Link>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
}